import React, { useContext, useState, useRef } from "react";
import { Box, ResponsiveContext, Text } from "grommet";
import AppLayout from "../../../components/layouts/AppLayout";
import { formatDate } from "../../../utils/dates";
import { formatCurrency } from "../../../utils/numbers";
import { csvFilename, getCSVConfig } from "../../../utils/csv";
import { handleErrors, handleFileDownloadErrors } from "../../../utils/errors";
import { useRouter } from "next/router";
import { formatName } from "../../../utils/names";
import { NotificationsContext } from "../../../components/NotificationsProvider";
import { DialogContext } from "../../../components/DialogProvider";
import DataTableWithBackendSearch from "../../../components/DataTableWithBackendSearch";
import { getQueryString } from "../../../utils/search";
import { useSWRConfig } from "swr";
import CustomerTag from "../../../components/CustomerTag";
import CustomerLabel from "../../../components/CustomerLabel";
import { useSession } from "next-auth/react";
import { getFormattedPeriodLabel, getFrequencyLabel } from "../../../components/pages/draft-invoices/edit/RecurringScheduleForm";
import {
    IconX as IconClose
} from '@tabler/icons-react';
import IconSync from '../../../components/icons/IconRefreshClockwise';
import Icon from "../../../components/Icon";
import OrderTag from "../../../components/OrderTag";
import Button from "../../../components/Button";
import { downloadBlobAsCsvFile } from "../../../lib/exportcsv";

function ScheduledInvoices() {
    const {data: session} = useSession();
    const {company} = session || {};

    return (
        <AppLayout
            title={"Invoices"}
            contentTitle="Scheduled"
            hasStickyDesktopPageHeader
        >
            <ScheduledInvoicesList
                hasShopifyLinked={company?.has_shopify_linked}
            />
        </AppLayout>
    );
}

const BULK_BATCH_SIZE = 20;

// Helper to chunk array into batches
const chunkArray = (array, size) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
        chunks.push(array.slice(i, i + size));
    }
    return chunks;
};

const ScheduledInvoicesList = ({hasShopifyLinked}) => {
    const router = useRouter();
    const { mutate } = useSWRConfig();
    const [selectedOrders, setSelectedOrders] = useState([]);
    const notificationCtx = useContext(NotificationsContext);
    const [queryUrl, setQueryUrl] = useState('');
    const [csvQueryUrl, setCsvQueryUrl] = useState();
    const resetSelectedRowsRef = useRef(null);
    const size = useContext(ResponsiveContext);
    const isMobile = size === "small";
    const {data: session} = useSession();
    const {company} = session || {};

    const getScheduledCSV = ({callback, url}) => {
        const exportError = {type: 'error', message: 'Unable to download csv data. Try again later.'};
        fetch(url, {
            method: 'GET'
        })
            .then(handleFileDownloadErrors)
            .then((data) => {
                data ?
                    downloadBlobAsCsvFile(
                        data,
                        csvFilename('recurring_invoice_export', { businessName: company?.business_name || company?.legal_name })) :
                    notificationCtx.showNotification(exportError);
                callback && callback();
            })
            .catch(e => notificationCtx.showNotification(exportError));
    };


    const columns = isMobile ? [
        {
            selector: row => row.id,
            cell: (datum) => {
                return ( // spamming with data-tag="allowRowEvents" otherwise the cell doesn't recognize onRowClicked event
                    <Box data-tag="allowRowEvents" fill="horizontal" justify="between" direction="row" gap="small" align="center">
                        {/* Shrinks and wraps so a long schedule line cannot push the amount past the screen. */}
                        <Box data-tag="allowRowEvents" flex style={{ minWidth: 0 }}>
                            <CustomerLabel data-tag="allowRowEvents" customerId={datum?.recipient_id} />
                            <Text  data-tag="allowRowEvents" size="small" color="text-weak" wordBreak="break-word">{datum?.identifier} • {datum?.draft_series_id ? `Send ${getFrequencyLabel(datum?.draft_series, true)} on ${getFormattedPeriodLabel(datum?.draft_series)}` : `Send on ${datum?.formatted_issued_at}`}</Text>
                        </Box>
                        <Box data-tag="allowRowEvents" flex={false}>
                            <Text data-tag="allowRowEvents" wordBreak="keep-all">{formatCurrency(datum?.total_amount, datum?.currency)}</Text>
                        </Box>
                    </Box>
                )
            }
        }
    ] : [
        {
            selector: row => row.customer_name,
            name: "Customer",
            sortable: true,
            grow: 2,
            sortField: 'company_name',
            cell: (datum) => datum?.recipient_id && (<CustomerTag id={datum.recipient_id} inline />)
        },
        {
            selector: row => row.identifier,
            name: "Invoice #",
            sortable: true,
            sortField: 'identifier',
            cell: (datum) => <OrderTag id={datum.id} />
        },
        {
            selector: row => row.total_amount,
            name: "Amount",
            right: true,
            type: "currency",
            sortable: true,
            sortField: 'total_amount',
            cell: (datum) => formatCurrency(datum.total_amount, datum.currency),
        },
        {
            selector: row => row.issued_at,
            name: "Send on",
            shrink: 1,
            sortable: true,
            sortField: 'issued_at',
            cell: (datum) => datum.formatted_issued_at,
        },
        ...(hasShopifyLinked ? [] : [{ // do not show type for shopify merchants
            selector: row => row.frequency,
            name: "Frequency",
            grow: 2,
            sortField: 'frequency',
            cell: (datum) => datum?.draft_series_id ? (
                <Box direction="row" align="start" gap="xsmall">
                    <Box flex={false} margin={{top: 'xxsmall'}}>
                        <Icon color="text-weak" SelectedIcon={IconSync} size="xsmall" />
                    </Box>
                    <Box>
                        <Text>
                            {`${getFrequencyLabel(datum?.draft_series)} on ${getFormattedPeriodLabel(datum?.draft_series)}`}
                        </Text>
                        {datum?.draft_series?.end_date ? (
                            <Text size="xsmall" color="text-weak">
                                Until {formatDate(datum?.draft_series?.end_date, false, true)}
                            </Text>
                        ) : (<></>)}
                    </Box>
                </Box>
            ) : '',
        }]),
    ];

    const bulkCancelSend = async () => {
        const orderIds = selectedOrders.map((order) => order.id);
        const batches = chunkArray(orderIds, BULK_BATCH_SIZE);

        let totalCancelled = 0;
        const allErrors = [];

        for (let i = 0; i < batches.length; i++) {
            const batchIds = batches[i].join(",");
            try {
                const response = await fetch(`/api/draft-invoices/bulk`, {
                    method: "PUT",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ids: batchIds, scheduled: false}),
                });
                await handleErrors(response);
                totalCancelled += batches[i].length;
            } catch (err) {
                // On batch failure, add all IDs in this batch as errors
                batches[i].forEach(id => {
                    const order = selectedOrders.find(o => o.id === id);
                    allErrors.push(order?.identifier || id);
                });
            }
        }

        resetSelectedRowsRef.current();
        mutate(queryUrl);

        if (allErrors.length > 0) {
            notificationCtx.showNotification({
                type: totalCancelled === 0 ? 'error' : undefined,
                message: totalCancelled > 0
                    ? `${totalCancelled} cancelled. Failed: ${allErrors.join(', ')}`
                    : `Failed to cancel invoices. Please try again.`
            });
        }
    };

    return (
        <DialogContext.Consumer>
            {({ showDialog }) => (
                <DataTableWithBackendSearch
                    onRowClicked={(row) => router.push(row?.draft_series_id ? `/invoices/scheduled/recurring/${row.draft_series_id}` : `/invoices/scheduled/${row.id}`)}
                    columns={columns}
                    resetSelectedRowsRef={resetSelectedRowsRef}
                    emptyListMessage="No scheduled invoices"
                    dataName={"invoices"}
                    tab="scheduled"
                    setQueryUrlCallback={(url) => {
                        const queryParams = url.split("?")[1];
                        setCsvQueryUrl("/api/draft-series/csv?" + queryParams);
                        setQueryUrl(url);
                    }}
                    reshape={(data) => {
                        if (!data) return null;
                        return data.orders.map((order) => ({
                            ...order,
                            created_at: new Date(order.created_at),
                            formatted_created_at: formatDate(order.created_at, false, true),
                            formatted_issued_at: formatDate(order.issued_at, false, true),
                            company_name: order.recipient
                                ? order.recipient.company_name
                                : "-",
                            customer_name: formatName(
                                order.recipient,
                                true,
                                "-"
                            ),
                            order_number: order.identifier,
                        }));
                    }}
                    getUrl={(props) =>
                        getQueryString({
                            ...props,
                            baseUrl: "api/draft-invoices/scheduled",
                        })
                    }
                    selectableRows
                    selectedControls={
                        <Box direction="row" gap="xsmall" justify="end">
                            <Box>
                                <Button
                                    event={{section: 'scheduled_invoice_list', element: 'cancel_send'}}
                                    icon={
                                        <Icon
                                            SelectedIcon={IconClose}
                                            size="xxsmall"
                                        />
                                    }
                                    secondary
                                    label={<Text>Cancel send</Text>}
                                    gap="xsmall"
                                    onClick={() => {
                                        showDialog({
                                            title: "Cancel send",
                                            message: selectedOrders.length > 1
                                                ? `Move these ${selectedOrders.length} invoices back to drafts?`
                                                : "Move this invoice back to drafts?",
                                            callback: bulkCancelSend,
                                            ctaType: 'error'
                                        });
                                    }}
                                    hoverIndicator
                                    align="end"
                                />
                            </Box>
                        </Box>
                    }
                    onSelectedRowsChange={(data) =>
                        data?.selectedRows &&
                        setSelectedOrders(data.selectedRows)
                    }
                    onExportCSVClicked={() => getScheduledCSV({ url: csvQueryUrl })}
                    exportConfigs={getCSVConfig(columns)}
                    dataTableWrapperProps={{ margin: "none" }}
                />
            )}
        </DialogContext.Consumer>
    );
};

export default ScheduledInvoices;
